export type PageResponse<T> = {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
};

export type ApiValidationError = {
  field: string;
  message: string;
};

export type ApiErrorResponse = {
  errorCode?: string;
  message?: string;
  path?: string;
  requestId?: string;
  timestamp?: string;
  validationErrors?: ApiValidationError[];
};

export type LeaveStatus =
  | "DRAFT"
  | "PENDING_APPROVAL"
  | "APPROVED"
  | "REJECTED"
  | "CANCELLED"
  | "RECALLED";

export type ApprovalAction =
  | "SAVED_AS_DRAFT"
  | "DRAFT_UPDATED"
  | "SUBMITTED"
  | "APPROVED"
  | "REJECTED"
  | "CANCELLED"
  | "RECALLED"
  | "EARLY_RETURN";

export type LeaveRecallRequest = {
  /** The date the employee actually returned (exclusive — last day of leave is actualReturnDate - 1) */
  actualReturnDate: string;
  reason?: string | null;
};

export type LeavePlanStatus = "PLANNED" | "SUBMITTED" | "CANCELLED";

export type LeaveDayCountMode = "WORKING_DAYS" | "CALENDAR_DAYS";

export type LeaveApplicableGender = "ALL" | "MALE" | "FEMALE";

export type CarryForwardExpiryMode = "NONE" | "MONTHS_AFTER_YEAR_END";

export type CurrentEmployee = {
  id: number;
  employeeNumber: string;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  email?: string | null;
  departmentName?: string | null;
  jobTitle?: string | null;
  gender?: string | null;
  employmentType?: string | null;
  employeeCategory?: string | null;
};

export type EmployeeOption = {
  id: number;
  employeeNumber: string;
  employeeName?: string | null;
  fullName?: string | null;
  name?: string | null;
  departmentName?: string | null;
  jobTitle?: string | null;
};

export type RefLookup = {
  id: number;
  lookupType: string;
  code: string;
  name: string;
  description?: string | null;
  sortOrder: number;
  active: boolean;
};

export type LeaveType = {
  id: number;
  code: string;
  name: string;
  description?: string | null;

  paid: boolean;
  requiresBalance: boolean;

  annualEntitlementDays: number;
  maxDaysPerRequest?: number | null;

  requiresAttachment: boolean;

  dayCountMode: LeaveDayCountMode;
  excludeWeekends: boolean;
  excludePublicHolidays: boolean;
  requiresReliefEmployee: boolean;
  minimumNoticeDays?: number | null;

  applicableGender: LeaveApplicableGender;

  allowsCarryForward: boolean;
  maxCarryForwardDays?: number | null;
  carryForwardExpiryMode: CarryForwardExpiryMode;
  carryForwardExpiryMonthsAfterYearEnd?: number | null;

  /**
   * When true, employees who match no entitlement rule still see this leave type
   * and receive annualEntitlementDays as their default balance.
   */
  allowUnmatchedEmployees: boolean;

  /**
   * When true, this leave type's balance appears on the employee dashboard summary.
   * Toggled per leave type from the Leave Type admin screen.
   */
  showInDashboard: boolean;

  active: boolean;
};

export type LeaveDashboardSummaryBreakdown = {
  leaveTypeId: number;
  leaveTypeCode: string;
  leaveTypeName: string;
  entitledDays: number;
  earnedDays?: number | null;
  broughtForwardDays?: number | null;
  pendingDays: number;
  usedDays: number;
  remainingDays: number;
  applicableGender?: LeaveApplicableGender | null;
};

export type LeaveDashboardSummaryResponse = {
  totalRemainingDays: number;
  totalEntitledDays: number;
  totalUsedDays: number;
  breakdown: LeaveDashboardSummaryBreakdown[];
};

export type EligibleLeaveType = {
  /** API returns leaveTypeId (primary). Some older builds may return id — handle both. */
  leaveTypeId: number;
  leaveTypeCode: string;
  leaveTypeName: string;
  /** backward-compat aliases — may be present depending on backend version */
  id?: number | null;
  code?: string | null;
  name?: string | null;

  ruleId?: number | null;
  ruleName?: string | null;

  description?: string | null;

  paid?: boolean;
  requiresBalance?: boolean;

  entitledDays: number;
  maxDaysPerRequest?: number | null;

  requiresAttachment: boolean;
  dayCountMode: LeaveDayCountMode;
  excludeWeekends: boolean;
  excludePublicHolidays: boolean;
  requiresReliefEmployee?: boolean;
  minimumNoticeDays?: number | null;

  applicableGender?: LeaveApplicableGender;

  allowsCarryForward?: boolean;
  maxCarryForwardDays?: number | null;
  carryForwardExpiryMode?: CarryForwardExpiryMode;
  carryForwardExpiryMonthsAfterYearEnd?: number | null;

  /** Balance fields — returned when backend enriches eligible types with current balance */
  openingBalance?: number | null;
  broughtForwardDays?: number | null;
  earnedDays?: number | null;
  pendingDays?: number | null;
  usedDays?: number | null;
  forfeitedDays?: number | null;
  remainingDays?: number | null;
};

/** Normalise either API shape into a stable object used by forms */
export function normaliseEligible(t: EligibleLeaveType) {
  return {
    ...t,
    id: t.leaveTypeId ?? t.id ?? 0,
    code: t.leaveTypeCode ?? t.code ?? "",
    name: t.leaveTypeName ?? t.name ?? "",
    remainingDays: t.remainingDays ?? null,
    pendingDays: t.pendingDays ?? null,
    usedDays: t.usedDays ?? null,
  };
}

export type LeaveTypeCreateRequest = {
  code: string;
  name: string;
  description?: string | null;

  paid?: boolean;
  requiresBalance?: boolean;

  annualEntitlementDays?: number | null;
  maxDaysPerRequest?: number | null;

  requiresAttachment?: boolean;

  dayCountMode?: LeaveDayCountMode;
  excludeWeekends?: boolean;
  excludePublicHolidays?: boolean;
  requiresReliefEmployee?: boolean;
  minimumNoticeDays?: number | null;

  applicableGender?: LeaveApplicableGender;

  allowsCarryForward?: boolean;
  maxCarryForwardDays?: number | null;
  carryForwardExpiryMode?: CarryForwardExpiryMode;
  carryForwardExpiryMonthsAfterYearEnd?: number | null;

  allowUnmatchedEmployees?: boolean;
  showInDashboard?: boolean;

  active?: boolean;
};

export type LeaveTypeUpdateRequest = Partial<LeaveTypeCreateRequest>;

export type LeaveBalance = {
  id: number;

  employeeId: number;
  employeeNumber?: string | null;
  employeeName?: string | null;

  leaveTypeId: number;
  leaveTypeCode: string;
  leaveTypeName: string;

  leaveYear: number;

  openingBalance: number;
  entitledDays: number;
  earnedDays: number;
  broughtForwardDays: number;

  carriedForwardFromYear?: number | null;
  carryForwardExpiresOn?: string | null;
  carryForwardExpiredDays: number;

  pendingDays: number;
  usedDays: number;
  forfeitedDays: number;
  remainingDays: number;
};

/** Manual correction of one employee's balance for one leave type/year. */
export type LeaveBalanceAdjustmentRequest = {
  employeeId: number;
  leaveTypeId: number;
  leaveYear: number;
  entitledDays: number;
  broughtForwardDays: number;
  /** Optional -- omit to leave the balance's existing used-days figure untouched. Set it
   *  when loading an opening balance mid-year for an employee who had already used some
   *  of this leave type before the balance was entered into the system. */
  usedDays?: number;
};

export type LeaveBalanceMatrixLeaveTypeColumn = {
  leaveTypeId: number;
  leaveTypeCode: string;
  leaveTypeName: string;
  applicableGender: LeaveApplicableGender;
};

export type LeaveBalanceMatrixCell = {
  leaveTypeId: number;
  leaveTypeName: string;
  applicable: boolean;

  openingBalance?: number | null;
  entitledDays?: number | null;
  earnedDays?: number | null;
  broughtForwardDays?: number | null;

  carriedForwardFromYear?: number | null;
  carryForwardExpiresOn?: string | null;
  carryForwardExpiredDays?: number | null;

  pendingDays?: number | null;
  usedDays?: number | null;
  forfeitedDays?: number | null;
  remainingDays?: number | null;
};

export type LeaveBalanceMatrixEmployeeRow = {
  employeeId: number;
  employeeNumber: string;
  employeeName: string;

  departmentName?: string | null;
  jobTitle?: string | null;

  /** Split fields (newer backend versions) */
  genderId?: number | null;
  genderCode?: string | null;
  genderName?: string | null;
  /** Single-string field (older/current backend versions) */
  gender?: string | null;

  employmentTypeId?: number | null;
  employmentTypeCode?: string | null;
  employmentTypeName?: string | null;
  /** Single-string field fallback */
  employmentType?: string | null;

  employeeCategoryId?: number | null;
  employeeCategoryCode?: string | null;
  employeeCategoryName?: string | null;
  /** Single-string field fallback */
  employeeCategory?: string | null;

  /** API returns this as `cells` */
  cells: LeaveBalanceMatrixCell[];
};

export type LeaveBalanceMatrixResponse = {
  leaveYear: number;
  /** API returns this as `columns` */
  columns: LeaveBalanceMatrixLeaveTypeColumn[];
  /** API returns this as `rows` */
  rows: LeaveBalanceMatrixEmployeeRow[];

  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
};

export type LeaveApplicationSummary = {
  id: number;
  applicationNo: string;

  employeeId: number;
  employeeName: string;
  /** API field name is `departmentName` */
  departmentName?: string | null;

  leaveTypeId: number;
  leaveTypeName: string;

  workflowCode?: string | null;
  currentStepOrder?: number | null;
  currentStepName?: string | null;

  startDate: string;
  endDate: string;
  requestedDays: number;

  status: LeaveStatus;

  /** Whether this leave type mandates a supporting document */
  requiresAttachment?: boolean | null;
  /** Whether the applicant actually uploaded a document */
  hasAttachment?: boolean | null;
  /** Direct download URL usable by the current approver */
  attachmentDownloadUrl?: string | null;

  submittedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
};

export type LeaveApprovalHistory = {
  id: number;
  /** API field name is `actorId` */
  actorId?: number | null;
  actorUsername?: string | null;
  action: ApprovalAction;
  comments?: string | null;
  actionAt: string;
  sequenceNo: number;
};

export type LeaveApplicationDetails = {
  id: number;
  applicationNo: string;

  employeeId: number;
  employeeNumber: string;
  employeeName: string;
  /** API field name is `departmentName` */
  departmentName?: string | null;
  /** API field name is `jobTitle` */
  jobTitle?: string | null;

  leaveTypeId: number;
  leaveTypeCode: string;
  leaveTypeName: string;

  workflowId?: number | null;
  workflowCode?: string | null;
  currentStepOrder?: number | null;
  currentStepName?: string | null;

  startDate: string;
  endDate: string;
  requestedDays: number;

  reason?: string | null;
  status: LeaveStatus;

  /** API field name is `currentApproverId` */
  currentApproverId?: number | null;
  currentApproverUsername?: string | null;

  reliefEmployeeId?: number | null;
  reliefEmployeeName?: string | null;

  attachmentPath?: string | null;
  /** Authenticated download URL for the attachment — e.g. /api/hr/leave/attachments/22 */
  attachmentDownloadUrl?: string | null;
  remarks?: string | null;

  /** Recall / early-return fields — populated when status is RECALLED */
  actualReturnDate?: string | null;
  actualDaysUsed?: number | null;
  recalledAt?: string | null;
  recallReason?: string | null;

  submittedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  cancelledAt?: string | null;

  approvalHistory: LeaveApprovalHistory[];
};

export type LeaveApplyRequest = {
  leaveTypeId: number;
  startDate: string;
  endDate: string;
  reason?: string | null;
  reliefEmployeeId?: number | null;
  attachmentPath?: string | null;
};

export type LeaveApprovalRequest = {
  comments?: string | null;
};

export type ReliefEmployeeOption = {
  /** API field name is `employeeId` */
  employeeId: number;
  employeeNumber: string;
  /** API field name is `fullName` */
  fullName: string;
  departmentName?: string | null;
  jobTitle?: string | null;
};

export type LeaveSetupStatus = {
  complete: boolean;
  hasLeaveTypes: boolean;
  hasEntitlementRules: boolean;
  hasHolidays: boolean;
  hasLeaveYearPolicy: boolean;
  message: string;
};

export type LeaveBalanceRecalculationResponse = {
  leaveYear: number | null;
  employeesProcessed: number;
  balancesCreatedOrVerified: number;
  employeesFailed: number;
  message: string;
};

export type Holiday = {
  id: number;
  holidayDate: string;
  name: string;
  description?: string | null;
  recurringAnnually: boolean;
  active: boolean;
};

export type HolidayCreateRequest = {
  holidayDate: string;
  name: string;
  description?: string | null;
  recurringAnnually?: boolean;
  active?: boolean;
};

export type HolidayUpdateRequest = Partial<HolidayCreateRequest>;

export type HolidayImportResponse = {
  imported: number;
  skipped: number;
  errors: {
    row: number;
    message: string;
  }[];
};

export type LeaveRuleOption = {
  id: number;
  code?: string | null;
  name: string;
};

export type LeaveEntitlementRule = {
  id: number;

  leaveTypeId: number;
  leaveTypeCode: string;
  leaveTypeName: string;

  ruleName: string;

  employeeId?: number | null;
  employeeName?: string | null;

  roleId?: number | null;
  roleName?: string | null;

  departmentId?: number | null;
  departmentName?: string | null;

  employeeCategoryId?: number | null;
  employeeCategoryCode?: string | null;
  employeeCategoryName?: string | null;

  genderId?: number | null;
  genderCode?: string | null;
  genderName?: string | null;

  employmentTypeId?: number | null;
  employmentTypeCode?: string | null;
  employmentTypeName?: string | null;

  entitledDays: number;
  maxDaysPerRequest?: number | null;

  requiresAttachment?: boolean | null;
  dayCountMode?: LeaveDayCountMode | null;
  excludeWeekends?: boolean | null;
  excludePublicHolidays?: boolean | null;

  priority: number;
  active: boolean;
};

export type LeaveEntitlementRuleCreateRequest = {
  leaveTypeId: number;
  ruleName: string;

  employeeId?: number | null;
  roleId?: number | null;
  departmentId?: number | null;

  employeeCategoryId?: number | null;
  genderId?: number | null;
  employmentTypeId?: number | null;

  entitledDays: number;
  maxDaysPerRequest?: number | null;

  requiresAttachment?: boolean | null;
  dayCountMode?: LeaveDayCountMode | null;
  excludeWeekends?: boolean | null;
  excludePublicHolidays?: boolean | null;

  priority?: number;
  active?: boolean;
};

export type LeaveEntitlementRuleUpdateRequest =
  Partial<LeaveEntitlementRuleCreateRequest>;

export type LeaveYearPolicy = {
  id: number;
  leaveYear: number;
  startDate: string;
  endDate: string;
  active: boolean;
  closed: boolean;
};

export type LeaveYearPolicyCreateRequest = {
  leaveYear: number;
  startDate: string;
  endDate: string;
  active?: boolean;
  closed?: boolean;
};

export type LeaveYearPolicyUpdateRequest =
  Partial<LeaveYearPolicyCreateRequest>;

export type LeaveYearRolloverResult = {
  fromYear: number;
  toYear: number;
  employeesProcessed: number;
  balancesCreated: number;
  balancesSkipped: number;
  employeesFailed: number;
  errors: {
    employeeId?: number;
    employeeNumber?: string;
    leaveTypeCode?: string;
    message: string;
  }[];
};

export type LeavePlan = {
  id: number;

  employeeId: number;
  employeeName: string;
  departmentName?: string | null;

  leaveTypeId: number;
  leaveTypeCode: string;
  leaveTypeName: string;

  planYear: number;
  startDate: string;
  endDate: string;
  plannedDays: number;

  status: LeavePlanStatus;
  notes?: string | null;

  reminderEnabled: boolean;
  reminderDaysBefore?: number | null;
  reminderSent: boolean;
  reminderSentAt?: string | null;
};

export type LeavePlanCreateRequest = {
  leaveTypeId: number;
  startDate: string;
  endDate: string;
  notes?: string | null;
  reminderEnabled?: boolean;
  reminderDaysBefore?: number | null;
};

export type LeavePlanUpdateRequest = Partial<LeavePlanCreateRequest>;

export type LeaveEntry = {
  applicationId: number;
  applicationNo: string;
  employeeId: number;
  employeeName: string;
  departmentName?: string | null;
  leaveTypeId: number;
  leaveTypeName: string;
  startDate: string;
  endDate: string;
  requestedDays: number;
  currentApproverUsername?: string | null;
};

export type OfficialDutyResponse = {
  id: number;
  employeeId: number;
  employeeNumber?: string | null;
  employeeName?: string | null;
  departmentName?: string | null;
  startDate: string;
  endDate: string;
  purpose?: string | null;
  location?: string | null;
  notes?: string | null;
  recordedByUserId?: number | null;
  recordedByName?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type OfficialDutyCreateRequest = {
  employeeId: number;
  startDate: string;
  endDate: string;
  purpose?: string | null;
  location?: string | null;
  notes?: string | null;
};

export type OfficialDutyUpdateRequest = Partial<OfficialDutyCreateRequest>;

export type WhoIsOnLeaveResponse = {
  currentlyOnLeave: LeaveEntry[];
  pendingApproval: LeaveEntry[];
  upcomingLeave: LeaveEntry[];
  /** Employees currently on official duty (away from office but not on leave) */
  onOfficialDuty: OfficialDutyResponse[];
};